import React, { useState, useEffect } from 'react';
import { Client, Device } from '../types';
import { clientAPI } from '../services/api';
import AddressAutocomplete from './AddressAutocomplete';
import PhoneInput from './PhoneInput';
import Comments from './Comments';
import ClientUsers from './ClientUsers';
import BulkUpload from './BulkUploadDebug';
import jsPDF from 'jspdf';
import ClientInvitation from './ClientInvitation';
import autoTable from 'jspdf-autotable';

interface ClientFormProps {
  client?: Client;
  onSave: () => void;
  onCancel: () => void;
}

const ClientForm: React.FC<ClientFormProps> = ({ client, onSave, onCancel }) => {
  const [carriers, setCarriers] = useState(['Kore super sim', 'One NZ', 'SpaceX DTC', 'Telit', 'Hologram']);
  const [deviceTypes, setDeviceTypes] = useState(['Starlink', 'Shadow', 'TAT240', 'E-Bike', 'Scout', 'Eliko Tag', 'Eliko Anchour', 'Urovo Tablet', 'Teltonika Gateway', 'Teltonika Device', 'Ericson Gateway', 'ELA BLE', 'Fuel Dome']);
  const [applications, setApplications] = useState(['Canary', 'MyAsset', 'Shadow', '365Trac', 'Karma', 'NZ Steel Mine Site', 'Steel Serv']);
  const [modelVersions, setModelVersions] = useState(['v1.0', 'v2.0', 'v3.0', 'Pro', 'Standard', 'Advanced']);
  const [deviceManufacturers, setDeviceManufacturers] = useState(['Teltonika', 'Eliko', 'Urovo', 'Ericsson', 'SpaceX', 'Samsung', 'Apple', 'Huawei', 'Other']);
  const [hexValues, setHexValues] = useState(['0x01', '0x02', '0x03', '0xFF', '0xAA', '0xBB', '0xCC']);
  const [simDataPlans, setSimDataPlans] = useState(['1GB', '5GB', '10GB', '20GB', '50GB', '100GB', 'Unlimited']);

  const [formData, setFormData] = useState<Omit<Client, 'id'>>({
    name: '',
    company_name: '',
    point_of_contact: '',
    address: '',
    email: '',
    phone_cell: '',
    phone_work: '',
    user_count: 0,
    monthly_fee: 0,
    billing_type: 'total',
    imei_number: '',
    iccid: '',
    carrier_name: '',
    device_type: '',
    serial_number: '',
    signup_date: new Date().toISOString().split('T')[0],
    monthly_hours_allocated: 0,
    hourly_rate: 0
  } as any);

  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceSearchTerm, setDeviceSearchTerm] = useState('');
  const [nfcCount, setNfcCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const devicesPerPage = 10;
  const [newDevice, setNewDevice] = useState({
    serial_number: '',
    imei_number: '',
    iccid: '',
    sim_provider: '',
    sim_text_number: '',
    vehicle_id: '',
    application: '',
    model_version: '',
    device_type: '',
    hex_value: '',
    device_manufacturer: '',
    sim_data_plan: '',
    fleet: '',
  });
  const [loading, setLoading] = useState(false);
  const [showDeviceForm, setShowDeviceForm] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [showInvitation, setShowInvitation] = useState(false);
  const [sendInvitation, setSendInvitation] = useState(false);
  const [isEditingClient, setIsEditingClient] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name,
        company_name: client.company_name || '',
        point_of_contact: client.point_of_contact || '',
        address: client.address,
        email: client.email,
        phone_cell: client.phone_cell,
        phone_work: client.phone_work,
        user_count: client.user_count,
        monthly_fee: client.monthly_fee,
        billing_type: client.billing_type || 'total',
        imei_number: client.imei_number || '',
        iccid: client.iccid || '',
        carrier_name: client.carrier_name || '',
        device_type: client.device_type || '',
        serial_number: client.serial_number || '',
        signup_date: client.signup_date || new Date().toISOString().split('T')[0],
        monthly_hours_allocated: (client as any).monthly_hours_allocated || 0,
        hourly_rate: (client as any).hourly_rate || 0
      } as any);
      setDevices(client.devices || []);
      setNfcCount(client.nfcLabels?.label_count || 0);
      setIsEditingClient(false); // Start in read-only mode for existing clients
    } else {
      setIsEditingClient(true); // Start in edit mode for new clients
    }
  }, [client]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      console.log('Submitting client data:', formData);
      if (client?.id) {
        await clientAPI.update(client.id, formData);
        if (nfcCount !== (client.nfcLabels?.label_count || 0)) {
          await clientAPI.updateNFCLabels(client.id, nfcCount);
        }
        setIsEditingClient(false); // Exit edit mode after saving
      } else {
        const clientData = { ...formData, send_invitation: sendInvitation };
        const response = await clientAPI.create(clientData);
        console.log('Client created:', response.data);
        
        if (response.data.invitation_sent) {
          alert('Client created successfully and invitation email sent!');
        } else if (sendInvitation) {
          alert('Client created successfully but invitation email failed to send.');
        } else {
          alert('Client created successfully!');
        }
        
        onSave(); // For new clients, go back to list
      }
    } catch (error: any) {
      console.error('Error saving client:', error);
      alert('Error saving client: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      await clientAPI.addDevice(client.id, newDevice);
      setNewDevice({
        serial_number: '',
        imei_number: '',
        iccid: '',
        sim_provider: '',
        sim_text_number: '',
        vehicle_id: '',
        application: '',
        model_version: '',
        device_type: '',
        hex_value: '',
        device_manufacturer: '',
        sim_data_plan: '',
        fleet: '',
      });
      setShowDeviceForm(false);
      const response = await clientAPI.getById(client.id);
      setDevices(response.data.devices || []);
      setCurrentPage(1);
      setToast({ message: '✅ Device added successfully!', type: 'success' });
      setTimeout(() => setToast(null), 3000);
    } catch (error: any) {
      console.error('Error adding device:', error);
      setToast({ message: '❌ Error adding device: ' + (error.response?.data?.error || error.message), type: 'error' });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = () => {
    setShowBulkUpload(true);
  };

  const removeDuplicateDevices = async () => {
    if (!window.confirm('Are you sure you want to remove duplicate devices? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await clientAPI.removeDuplicates();
      
      alert(`Successfully removed ${response.data.duplicatesRemoved} duplicate devices`);
      
      // Refresh devices after successful removal
      if (client?.id) {
        const refreshResponse = await clientAPI.getById(client.id);
        setDevices(refreshResponse.data.devices || []);
        setCurrentPage(1);
      }
    } catch (error: any) {
      console.error('Error removing duplicates:', error);
      alert('Error removing duplicates: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDevice = async (device: Device) => {
    if (!window.confirm(`Are you sure you want to delete device ${device.serial_number}? This action cannot be undone.`)) {
      return;
    }
    
    setLoading(true);
    try {
      await clientAPI.deleteDevice(device.id!);
      
      // Refresh devices after successful deletion
      if (client?.id) {
        const refreshResponse = await clientAPI.getById(client.id);
        setDevices(refreshResponse.data.devices || []);
        setCurrentPage(1);
      }
      
      setSelectedDevice(null);
      alert('Device deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting device:', error);
      alert('Error deleting device: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEditDevice = (device: Device) => {
    setEditingDevice({ ...device });
    setSelectedDevice(null);
  };

  const handleUpdateDevice = async () => {
    if (!editingDevice || !client?.id) return;
    setLoading(true);
    try {
      await clientAPI.updateDevice(client.id, editingDevice.id!, {
        serial_number: editingDevice.serial_number,
        imei_number: editingDevice.imei_number,
        iccid: editingDevice.iccid,
        sim_provider: editingDevice.sim_provider,
        sim_text_number: editingDevice.sim_text_number,
        vehicle_id: editingDevice.vehicle_id,
        application: editingDevice.application,
        model_version: editingDevice.model_version,
        device_type: editingDevice.device_type,
        hex_value: editingDevice.hex_value,
        device_manufacturer: editingDevice.device_manufacturer,
        sim_data_plan: editingDevice.sim_data_plan,
        fleet: (editingDevice as any).fleet,
      });
      const response = await clientAPI.getById(client.id);
      setDevices(response.data.devices || []);
      setEditingDevice(null);
      alert('Device updated successfully!');
    } catch (error: any) {
      console.error('Error updating device:', error);
      alert('Error updating device: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadClientPDF = () => {
    if (!client) return;
    
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.text('Client Information Report', 20, 20);
    
    // Client Details
    doc.setFontSize(16);
    doc.text('Client Details', 20, 40);
    doc.setFontSize(12);
    doc.text(`Name: ${client.name}`, 20, 55);
    doc.text(`Company: ${client.company_name || 'N/A'}`, 20, 65);
    doc.text(`Contact: ${client.point_of_contact || 'N/A'}`, 20, 75);
    doc.text(`Email: ${client.email}`, 20, 85);
    doc.text(`Cell Phone: ${client.phone_cell}`, 20, 95);
    doc.text(`Work Phone: ${client.phone_work || 'N/A'}`, 20, 105);
    doc.text(`Address: ${client.address}`, 20, 115);
    
    // Devices Section
    if (devices.length > 0) {
      doc.setFontSize(16);
      doc.text('Devices', 20, 135);
      
      const deviceData = devices.map((device, index) => [
        `Device ${index + 1}`,
        device.serial_number || 'N/A',
        device.imei_number || 'N/A',
        device.sim_provider || 'N/A',
        device.device_type || 'N/A',
        device.application || 'N/A'
      ]);
      
      autoTable(doc, {
        head: [['Device', 'Serial Number', 'IMEI', 'SIM Provider', 'Type', 'Application']],
        body: deviceData,
        startY: 145,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [30, 58, 138] }
      });
    }
    
    doc.save(`${client.name.replace(/[^a-z0-9]/gi, '_')}_report.pdf`);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', padding: '24px' }}>
      {toast && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 10000, padding: '14px 24px', borderRadius: '12px', background: toast.type === 'success' ? '#f0fdf4' : '#fef2f2', color: toast.type === 'success' ? '#16a34a' : '#dc2626', border: `1px solid ${toast.type === 'success' ? '#bbf7d0' : '#fecaca'}`, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', fontSize: '14px', fontWeight: 600 }}>
          {toast.message}
        </div>
      )}
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: 700, margin: 0, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {client ? `👤 ${client.name}` : '👤 Add New Client'}
          </h1>
          <p style={{ color: '#888', margin: '4px 0 0 0', fontSize: '14px' }}>
            {client ? 'View client information and manage devices' : 'Create a new client profile'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onCancel} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>
            {client ? '← Back' : 'Cancel'}
          </button>
          {client && (
            <button onClick={() => setShowInvitation(true)} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#059669', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
              📧 Invite to Portal
            </button>
          )}
          {client && (
            <button onClick={downloadClientPDF} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '14px', color: '#374151', fontWeight: 500 }}>
              📄 PDF
            </button>
          )}
          {(!client || isEditingClient) && (
            <button onClick={handleSubmit} disabled={loading} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
              {loading ? 'Saving...' : 'Save Client'}
            </button>
          )}
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', marginBottom: '20px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: '#1f2937' }}>📋 Client Information</h3>
          {client && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setIsEditingClient(!isEditingClient)}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer', background: isEditingClient ? '#fef2f2' : '#eff6ff', color: isEditingClient ? '#dc2626' : '#2563eb' }}
              >
                {isEditingClient ? '✕ Cancel Edit' : '✏️ Edit Details'}
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('⚠️ WARNING: This will permanently delete this client and ALL associated devices and users. This action cannot be undone. Are you sure?')) return;
                  if (!window.confirm('⚠️ FINAL CONFIRMATION: Click OK to permanently delete this client, or Cancel to abort.')) return;
                  const confirmName = prompt(`Type the client name "${client.name}" to confirm deletion:`);
                  if (confirmName !== client.name) {
                    alert('Client name does not match. Deletion cancelled.');
                    return;
                  }
                  try {
                    await clientAPI.delete(client.id!);
                    alert('Client deleted successfully');
                    onSave();
                  } catch (error) {
                    alert('Failed to delete client');
                  }
                }}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #fecaca', fontSize: '13px', fontWeight: 600, cursor: 'pointer', background: '#fef2f2', color: '#dc2626' }}
              >
                🗑️ Delete
              </button>
            </div>
          )}
        </div>
        
        <form id="client-form" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
            {/* Left Column - Form Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>👤 Client Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required disabled={!!client && !isEditingClient} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '2px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: (client && !isEditingClient) ? '#f9fafb' : '#fff', color: (client && !isEditingClient) ? '#6b7280' : '#1f2937' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🏢 Company Name *</label>
                <input type="text" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} required disabled={!!client && !isEditingClient} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '2px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: (client && !isEditingClient) ? '#f9fafb' : '#fff', color: (client && !isEditingClient) ? '#6b7280' : '#1f2937' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>👨‍💼 Point of Contact *</label>
                <input type="text" value={formData.point_of_contact} onChange={(e) => setFormData({ ...formData, point_of_contact: e.target.value })} required disabled={!!client && !isEditingClient} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '2px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: (client && !isEditingClient) ? '#f9fafb' : '#fff', color: (client && !isEditingClient) ? '#6b7280' : '#1f2937' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📧 Email Address *</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required disabled={!!client && !isEditingClient} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '2px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: (client && !isEditingClient) ? '#f9fafb' : '#fff', color: (client && !isEditingClient) ? '#6b7280' : '#1f2937' }} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📍 Address *</label>
                <AddressAutocomplete value={formData.address} onChange={(address) => setFormData({ ...formData, address })} disabled={!!client && !isEditingClient} required />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📱 Cell Phone *</label>
                <PhoneInput value={formData.phone_cell} onChange={(value) => setFormData({ ...formData, phone_cell: value || '' })} disabled={!!client && !isEditingClient} required placeholder="Enter cell phone number" />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>☎️ Work Phone</label>
                <PhoneInput value={formData.phone_work} onChange={(value) => setFormData({ ...formData, phone_work: value || '' })} disabled={!!client && !isEditingClient} placeholder="Enter work phone number" />
              </div>
            </div>
            {/* Right Column - Map and Summary */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {formData.address && (
                <>
                  <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                    <img
                      src={`https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(formData.address)}&zoom=15&size=350x160&maptype=roadmap&markers=color:red%7C${encodeURIComponent(formData.address)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
                      alt="Location Map"
                      style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; const next = e.currentTarget.nextElementSibling as HTMLElement; if (next) next.style.display = 'flex'; }}
                    />
                    <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', height: '160px', color: '#9ca3af', fontSize: '14px', background: '#f9fafb' }}>📍 Map not available</div>
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '16px', border: '1px solid #e5e7eb' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 700, color: '#374151' }}>📋 Contact Summary</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                      {[
                        { icon: '🏢', label: 'Company', value: formData.company_name },
                        { icon: '👨‍💼', label: 'Contact', value: formData.point_of_contact },
                        { icon: '📧', label: 'Email', value: formData.email },
                        { icon: '📱', label: 'Cell', value: formData.phone_cell },
                        ...(formData.phone_work ? [{ icon: '☎️', label: 'Work', value: formData.phone_work }] : []),
                      ].map((r, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: '#fff', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                          <span style={{ color: '#6b7280', fontWeight: 500 }}>{r.icon} {r.label}</span>
                          <span style={{ color: '#1f2937', fontWeight: 600, textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.value || 'Not set'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {!client && (
            <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #0ea5e9' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sendInvitation}
                  onChange={(e) => setSendInvitation(e.target.checked)}
                  style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                />
                <span style={{ fontWeight: '500', color: '#1e3a8a' }}>
                  Send client portal invitation email
                </span>
              </label>
              <p style={{ margin: '8px 0 0 24px', fontSize: '14px', color: '#666' }}>
                The client will receive an email with instructions to register for the client portal and access their support tickets.
              </p>
            </div>
          )}

          {client?.id && (
            <Comments 
              clientId={client.id} 
              currentUser={localStorage.getItem('username') || 'Unknown User'} 
            />
          )}

          {client?.id && (
            <ClientUsers 
              clientId={client.id.toString()} 
              clientName={client.company_name || client.name} 
            />
          )}

          <div style={{ marginTop: '32px', backgroundColor: '#f9fafb', borderRadius: '12px', padding: '24px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '20px', color: '#333' }}>Billing Information</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>
                  Billing Type
                </label>
                <select
                  value={formData.billing_type || 'total'}
                  onChange={(e) => setFormData({ ...formData, billing_type: e.target.value as 'total' | 'per_device' })}
                  disabled={!isEditingClient && !!client}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px'
                  }}
                >
                  <option value="total">Total Monthly Fee</option>
                  <option value="per_device">Per Device Fee</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>
                  {formData.billing_type === 'per_device' ? 'Fee Per Device ($)' : 'Total Monthly Fee ($)'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.monthly_fee}
                  onChange={(e) => setFormData({ ...formData, monthly_fee: parseFloat(e.target.value) || 0 })}
                  disabled={!isEditingClient && !!client}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            {formData.billing_type === 'per_device' && devices.length > 0 && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#e0f2fe', borderRadius: '6px' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#0369a1' }}>
                  <strong>Total Monthly Cost:</strong> ${(formData.monthly_fee * devices.length).toFixed(2)} 
                  <span style={{ color: '#64748b', marginLeft: '8px' }}>
                    ({devices.length} devices × ${formData.monthly_fee.toFixed(2)})
                  </span>
                </p>
              </div>
            )}

            {/* Support Hours Configuration */}
            <div style={{ marginTop: '24px', padding: '20px', backgroundColor: '#eff6ff', borderRadius: '12px', border: '2px solid #bae6fd' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px' }}>⏱️</span>
                Support Hours Configuration
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                    Monthly Hours Allocated
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={(formData as any).monthly_hours_allocated || 0}
                    onChange={(e) => setFormData({ ...formData, monthly_hours_allocated: parseFloat(e.target.value) || 0 } as any)}
                    disabled={!isEditingClient && !!client}
                    placeholder="e.g., 50"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>Hours reset on 1st of each month</p>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                    Hourly Rate ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={(formData as any).hourly_rate || 0}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 } as any)}
                    disabled={!isEditingClient && !!client}
                    placeholder="e.g., 150.00"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>Rate for outside support hours</p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {client && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#333' }}>Devices ({devices.length})</h3>
              <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>Manage all devices associated with this client</p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowDeviceForm(!showDeviceForm)} className="btn btn-primary" style={{ padding: '10px 20px', borderRadius: '8px', fontWeight: '500' }}>+ Add Device</button>
              <button onClick={handleBulkUpload} className="btn btn-secondary" style={{ padding: '10px 20px', borderRadius: '8px', fontWeight: '500' }}>📤 Bulk Upload</button>
              <button onClick={removeDuplicateDevices} className="btn btn-secondary" style={{ padding: '10px 20px', borderRadius: '8px', fontWeight: '500' }}>🗑️ Remove Duplicates</button>
            </div>
          </div>

          <div style={{ marginBottom: '20px', padding: '16px', border: '2px solid #e5e7eb', borderRadius: '12px', backgroundColor: '#f9fafb' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Search Here</label>
            <input
              type="text"
              placeholder="🔍 Search devices..."
              value={deviceSearchTerm}
              onChange={(e) => setDeviceSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 16px',
                fontSize: '14px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: '#ffffff'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          {showDeviceForm && (
            <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '24px', border: '1px solid #e9ecef' }}>
              <h4 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600', color: '#333' }}>Add New Device</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <input type="text" placeholder="Fleet *" value={newDevice.fleet} onChange={(e) => setNewDevice({ ...newDevice, fleet: e.target.value })} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }} />
                <input type="text" placeholder="Serial Number" value={newDevice.serial_number} onChange={(e) => setNewDevice({ ...newDevice, serial_number: e.target.value })} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }} />
                <input type="text" placeholder="IMEI Number" value={newDevice.imei_number} onChange={(e) => setNewDevice({ ...newDevice, imei_number: e.target.value })} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }} />
                <input type="text" placeholder="ICCID Number" value={newDevice.iccid} onChange={(e) => setNewDevice({ ...newDevice, iccid: e.target.value })} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }} />
                <select value={newDevice.sim_provider} onChange={(e) => { if (e.target.value === 'ADD_NEW') { const newCarrier = prompt('Enter new Carrier:'); if (newCarrier && newCarrier.trim()) { setCarriers([...carriers, newCarrier.trim()]); setNewDevice({ ...newDevice, sim_provider: newCarrier.trim() }); } } else { setNewDevice({ ...newDevice, sim_provider: e.target.value }); } }} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                  <option value="">Select Carrier</option>
                  {carriers.map(carrier => <option key={carrier} value={carrier}>{carrier}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
                <input type="text" placeholder="SIM Text Number" value={newDevice.sim_text_number} onChange={(e) => setNewDevice({ ...newDevice, sim_text_number: e.target.value })} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }} />
                <input type="text" placeholder="Vehicle ID" value={newDevice.vehicle_id} onChange={(e) => setNewDevice({ ...newDevice, vehicle_id: e.target.value })} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }} />
                <select value={newDevice.application} onChange={(e) => { if (e.target.value === 'ADD_NEW') { const newApp = prompt('Enter new Application:'); if (newApp && newApp.trim()) { setApplications([...applications, newApp.trim()]); setNewDevice({ ...newDevice, application: newApp.trim() }); } } else { setNewDevice({ ...newDevice, application: e.target.value }); } }} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                  <option value="">Select Application</option>
                  {applications.map(app => <option key={app} value={app}>{app}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
                <select value={newDevice.model_version} onChange={(e) => { if (e.target.value === 'ADD_NEW') { const newModel = prompt('Enter new Model/Version:'); if (newModel && newModel.trim()) { setModelVersions([...modelVersions, newModel.trim()]); setNewDevice({ ...newDevice, model_version: newModel.trim() }); } } else { setNewDevice({ ...newDevice, model_version: e.target.value }); } }} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                  <option value="">Select Model/Version</option>
                  {modelVersions.map(model => <option key={model} value={model}>{model}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
                <select value={newDevice.device_type} onChange={(e) => { if (e.target.value === 'ADD_NEW') { const newType = prompt('Enter new Device Type:'); if (newType && newType.trim()) { setDeviceTypes([...deviceTypes, newType.trim()]); setNewDevice({ ...newDevice, device_type: newType.trim() }); } } else { setNewDevice({ ...newDevice, device_type: e.target.value }); } }} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                  <option value="">Select Device Type</option>
                  {deviceTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
                <select value={newDevice.hex_value} onChange={(e) => { if (e.target.value === 'ADD_NEW') { const newHex = prompt('Enter new Hex Value:'); if (newHex && newHex.trim()) { setHexValues([...hexValues, newHex.trim()]); setNewDevice({ ...newDevice, hex_value: newHex.trim() }); } } else { setNewDevice({ ...newDevice, hex_value: e.target.value }); } }} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                  <option value="">Select Hex Value</option>
                  {hexValues.map(hex => <option key={hex} value={hex}>{hex}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
                <select value={newDevice.device_manufacturer} onChange={(e) => { if (e.target.value === 'ADD_NEW') { const newManufacturer = prompt('Enter new Manufacturer:'); if (newManufacturer && newManufacturer.trim()) { setDeviceManufacturers([...deviceManufacturers, newManufacturer.trim()]); setNewDevice({ ...newDevice, device_manufacturer: newManufacturer.trim() }); } } else { setNewDevice({ ...newDevice, device_manufacturer: e.target.value }); } }} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                  <option value="">Select Manufacturer</option>
                  {deviceManufacturers.map(manufacturer => <option key={manufacturer} value={manufacturer}>{manufacturer}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
                <select value={newDevice.sim_data_plan} onChange={(e) => { if (e.target.value === 'ADD_NEW') { const newPlan = prompt('Enter new SIM Data Plan:'); if (newPlan && newPlan.trim()) { setSimDataPlans([...simDataPlans, newPlan.trim()]); setNewDevice({ ...newDevice, sim_data_plan: newPlan.trim() }); } } else { setNewDevice({ ...newDevice, sim_data_plan: e.target.value }); } }} className="form-control" style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                  <option value="">Select SIM Data Plan</option>
                  {simDataPlans.map(plan => <option key={plan} value={plan}>{plan}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
              </div>
              <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
                <button onClick={handleAddDevice} className="btn btn-primary" disabled={loading} style={{ padding: '10px 20px', borderRadius: '6px' }}>{loading ? 'Adding...' : 'Add Device'}</button>
                <button onClick={() => setShowDeviceForm(false)} className="btn btn-secondary" style={{ padding: '10px 20px', borderRadius: '6px' }}>Cancel</button>
              </div>
            </div>
          )}

          {devices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px dashed #dee2e6' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>📱</div>
              <h4 style={{ margin: '0 0 8px 0', color: '#6c757d' }}>No devices added yet</h4>
              <p style={{ fontSize: '14px', margin: 0 }}>Click "Add Device" to get started</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
              {devices.filter(d => {
                const search = deviceSearchTerm.toLowerCase();
                return !search || 
                  d.serial_number?.toLowerCase().includes(search) ||
                  d.imei_number?.toLowerCase().includes(search) ||
                  d.iccid?.toLowerCase().includes(search) ||
                  d.vehicle_id?.toLowerCase().includes(search) ||
                  d.sim_provider?.toLowerCase().includes(search) ||
                  d.device_type?.toLowerCase().includes(search) ||
                  d.application?.toLowerCase().includes(search) ||
                  d.hex_value?.toLowerCase().includes(search);
              }).map((device, index) => (
                <div
                  key={device.id}
                  onClick={() => setSelectedDevice(device)}
                  style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', cursor: 'pointer', overflow: 'hidden', transition: 'box-shadow 0.2s, border-color 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#2563eb'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                >
                  {/* Header */}
                  <div style={{ padding: '12px 14px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 600 }}>#{index + 1}</span>
                      <span style={{ fontSize: '15px', fontWeight: 700, color: device.vehicle_id ? '#1f2937' : '#f59e0b' }}>{device.vehicle_id || '⚠️ No Vehicle ID'}</span>
                    </div>
                    <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 600, background: device.sim_provider ? '#dcfce7' : '#fee2e2', color: device.sim_provider ? '#166534' : '#dc2626' }}>{device.sim_provider || 'No SIM'}</span>
                  </div>
                  {/* Body */}
                  <div style={{ padding: '12px 14px', fontSize: '12px', display: 'grid', gap: '6px' }}>
                    {(device as any).fleet && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#9ca3af' }}>Fleet</span>
                        <span style={{ color: '#7c3aed', fontWeight: 600 }}>{(device as any).fleet}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9ca3af' }}>Type</span>
                      <span style={{ color: '#374151', fontWeight: 600 }}>{device.device_type || 'N/A'}</span>
                    </div>
                    {device.application && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#9ca3af' }}>App</span>
                        <span style={{ color: '#374151', fontWeight: 600 }}>{device.application}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9ca3af' }}>Serial</span>
                      <span style={{ color: '#374151', fontWeight: 500, fontFamily: 'monospace', fontSize: '11px' }}>{device.serial_number || 'N/A'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9ca3af' }}>IMEI</span>
                      <span style={{ color: '#374151', fontWeight: 500, fontFamily: 'monospace', fontSize: '11px' }}>{device.imei_number || 'N/A'}</span>
                    </div>
                    {device.hex_value && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#9ca3af' }}>Hex</span>
                        <span style={{ color: '#7c3aed', fontWeight: 600, fontFamily: 'monospace', fontSize: '11px' }}>{device.hex_value}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedDevice && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Device Details</h3>
              <button onClick={() => setSelectedDevice(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', padding: '0', color: '#666' }}>×</button>
            </div>
            <div style={{ display: 'grid', gap: '15px' }}>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>FLEET</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{(selectedDevice as any).fleet || 'Not set'}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>SERIAL NUMBER</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.serial_number}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>IMEI NUMBER</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.imei_number}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>ICCID NUMBER</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.iccid || 'Not set'}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>SIM PROVIDER</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.sim_provider || 'Not set'}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>SIM TEXT NUMBER</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.sim_text_number || 'Not set'}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>VEHICLE ID</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.vehicle_id || 'Not set'}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>APPLICATION</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.application || 'Not set'}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>MODEL/VERSION</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.model_version || 'Not set'}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>DEVICE TYPE</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.device_type || 'Not set'}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>HEX VALUE</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.hex_value || 'Not set'}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>MANUFACTURER</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.device_manufacturer || 'Not set'}</div></div>
              <div><label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>SIM DATA PLAN</label><div style={{ fontSize: '16px', fontWeight: '500', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>{selectedDevice.sim_data_plan || 'Not set'}</div></div>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => handleDeleteDevice(selectedDevice)} className="btn btn-danger" style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}>Delete Device</button>
              <button onClick={() => handleEditDevice(selectedDevice)} className="btn btn-secondary">Edit Device</button>
              <button onClick={() => setSelectedDevice(null)} className="btn btn-primary">Close</button>
            </div>
          </div>
        </div>
      )}

      {editingDevice && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', maxWidth: '600px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Edit Device</h3>
              <button onClick={() => setEditingDevice(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', padding: '0', color: '#666' }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>Fleet</label>
                <input 
                  type="text" 
                  value={(editingDevice as any).fleet || ''} 
                  onChange={(e) => setEditingDevice({ ...editingDevice, fleet: e.target.value } as any)} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }} 
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>Serial Number</label>
                <input 
                  type="text" 
                  value={editingDevice.serial_number} 
                  onChange={(e) => setEditingDevice({ ...editingDevice, serial_number: e.target.value })} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }} 
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>IMEI Number</label>
                <input 
                  type="text" 
                  value={editingDevice.imei_number} 
                  onChange={(e) => setEditingDevice({ ...editingDevice, imei_number: e.target.value })} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }} 
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>ICCID Number</label>
                <input 
                  type="text" 
                  value={editingDevice.iccid} 
                  onChange={(e) => setEditingDevice({ ...editingDevice, iccid: e.target.value })} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }} 
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>SIM Provider</label>
                <select 
                  value={editingDevice.sim_provider} 
                  onChange={(e) => {
                    if (e.target.value === 'ADD_NEW') {
                      const newCarrier = prompt('Enter new Carrier:');
                      if (newCarrier && newCarrier.trim()) {
                        setCarriers([...carriers, newCarrier.trim()]);
                        setEditingDevice({ ...editingDevice, sim_provider: newCarrier.trim() });
                      }
                    } else {
                      setEditingDevice({ ...editingDevice, sim_provider: e.target.value });
                    }
                  }} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }}
                >
                  <option value="">Select Carrier</option>
                  {carriers.map(carrier => <option key={carrier} value={carrier}>{carrier}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>SIM Text Number</label>
                <input 
                  type="text" 
                  value={editingDevice.sim_text_number} 
                  onChange={(e) => setEditingDevice({ ...editingDevice, sim_text_number: e.target.value })} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }} 
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>Vehicle ID</label>
                <input 
                  type="text" 
                  value={editingDevice.vehicle_id} 
                  onChange={(e) => setEditingDevice({ ...editingDevice, vehicle_id: e.target.value })} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }} 
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>Application</label>
                <select 
                  value={editingDevice.application} 
                  onChange={(e) => {
                    if (e.target.value === 'ADD_NEW') {
                      const newApp = prompt('Enter new Application:');
                      if (newApp && newApp.trim()) {
                        setApplications([...applications, newApp.trim()]);
                        setEditingDevice({ ...editingDevice, application: newApp.trim() });
                      }
                    } else {
                      setEditingDevice({ ...editingDevice, application: e.target.value });
                    }
                  }} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }}
                >
                  <option value="">Select Application</option>
                  {applications.map(app => <option key={app} value={app}>{app}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>Model/Version</label>
                <select 
                  value={editingDevice.model_version} 
                  onChange={(e) => {
                    if (e.target.value === 'ADD_NEW') {
                      const newModel = prompt('Enter new Model/Version:');
                      if (newModel && newModel.trim()) {
                        setModelVersions([...modelVersions, newModel.trim()]);
                        setEditingDevice({ ...editingDevice, model_version: newModel.trim() });
                      }
                    } else {
                      setEditingDevice({ ...editingDevice, model_version: e.target.value });
                    }
                  }} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }}
                >
                  <option value="">Select Model/Version</option>
                  {modelVersions.map(model => <option key={model} value={model}>{model}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>Device Type</label>
                <select 
                  value={editingDevice.device_type} 
                  onChange={(e) => {
                    if (e.target.value === 'ADD_NEW') {
                      const newType = prompt('Enter new Device Type:');
                      if (newType && newType.trim()) {
                        setDeviceTypes([...deviceTypes, newType.trim()]);
                        setEditingDevice({ ...editingDevice, device_type: newType.trim() });
                      }
                    } else {
                      setEditingDevice({ ...editingDevice, device_type: e.target.value });
                    }
                  }} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }}
                >
                  <option value="">Select Device Type</option>
                  {deviceTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>Hex Value</label>
                <select 
                  value={editingDevice.hex_value} 
                  onChange={(e) => {
                    if (e.target.value === 'ADD_NEW') {
                      const newHex = prompt('Enter new Hex Value:');
                      if (newHex && newHex.trim()) {
                        setHexValues([...hexValues, newHex.trim()]);
                        setEditingDevice({ ...editingDevice, hex_value: newHex.trim() });
                      }
                    } else {
                      setEditingDevice({ ...editingDevice, hex_value: e.target.value });
                    }
                  }} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }}
                >
                  <option value="">Select Hex Value</option>
                  {hexValues.map(hex => <option key={hex} value={hex}>{hex}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>Device Manufacturer</label>
                <select 
                  value={editingDevice.device_manufacturer} 
                  onChange={(e) => {
                    if (e.target.value === 'ADD_NEW') {
                      const newManufacturer = prompt('Enter new Manufacturer:');
                      if (newManufacturer && newManufacturer.trim()) {
                        setDeviceManufacturers([...deviceManufacturers, newManufacturer.trim()]);
                        setEditingDevice({ ...editingDevice, device_manufacturer: newManufacturer.trim() });
                      }
                    } else {
                      setEditingDevice({ ...editingDevice, device_manufacturer: e.target.value });
                    }
                  }} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }}
                >
                  <option value="">Select Manufacturer</option>
                  {deviceManufacturers.map(manufacturer => <option key={manufacturer} value={manufacturer}>{manufacturer}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' }}>SIM Data Plan</label>
                <select 
                  value={editingDevice.sim_data_plan} 
                  onChange={(e) => {
                    if (e.target.value === 'ADD_NEW') {
                      const newPlan = prompt('Enter new SIM Data Plan:');
                      if (newPlan && newPlan.trim()) {
                        setSimDataPlans([...simDataPlans, newPlan.trim()]);
                        setEditingDevice({ ...editingDevice, sim_data_plan: newPlan.trim() });
                      }
                    } else {
                      setEditingDevice({ ...editingDevice, sim_data_plan: e.target.value });
                    }
                  }} 
                  className="form-control" 
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', width: '100%' }}
                >
                  <option value="">Select SIM Data Plan</option>
                  {simDataPlans.map(plan => <option key={plan} value={plan}>{plan}</option>)}
                  <option value="ADD_NEW" style={{ fontWeight: 'bold', color: '#007bff' }}>+ ADD NEW</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingDevice(null)} className="btn btn-secondary">Cancel</button>
              <button onClick={handleUpdateDevice} className="btn btn-primary" disabled={loading}>
                {loading ? 'Updating...' : 'Update Device'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvitation && client && (
        <ClientInvitation 
          client={client} 
          onClose={() => setShowInvitation(false)} 
        />
      )}

      {client && (
        <BulkUpload
          isOpen={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          clientId={client.id?.toString() || ''}
          onSuccess={() => {
            // Refresh devices after successful upload
            if (client.id) {
              clientAPI.getById(client.id).then(response => {
                setDevices(response.data.devices || []);
                setCurrentPage(1);
              });
            }
          }}
        />
      )}
    </div>
    </div>
  );
};

export default ClientForm;
